/**
 * ServiceChargesPanel
 *
 * Portal view of one-time service fees (email setup, Google Business Profile
 * claim, migrations). These use authorize-then-capture: confirming places a
 * HOLD on the card and collects nothing. Payment is taken only once the work
 * is delivered, and if we never deliver the hold is released.
 *
 * The copy is deliberate. Stripe warns that some card statements do not
 * distinguish a hold from a settled payment, so a customer who is not told
 * plainly will believe they have already been charged — and then think they
 * were charged twice when the capture lands.
 *
 * Rendered only when the customer has charges; it stays invisible otherwise.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Receipt,
  Loader2,
  CheckCircle,
  Clock,
  AlertCircle,
  ShieldCheck,
} from 'lucide-react';
import {
  listServiceCharges,
  authorizeServiceCharge,
  RequiresActionError,
  formatAmount,
  daysUntilExpiry,
  type ServiceCharge,
} from '../api/service-charges';
import { createBillingPortalSession } from '../api/billing';

const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;

export function ServiceChargesPanel() {
  const [charges, setCharges] = useState<ServiceCharge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [needsPaymentMethod, setNeedsPaymentMethod] = useState(false);
  const [openingBilling, setOpeningBilling] = useState(false);

  const fetchCharges = useCallback(async () => {
    try {
      setCharges(await listServiceCharges());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load services');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCharges();
  }, [fetchCharges]);

  /**
   * Complete a 3D Secure challenge.
   *
   * Stripe.js must be loaded from Stripe's own domain, so it is imported
   * dynamically rather than bundled. On success we do NOT mark the charge
   * authorized locally — the `amount_capturable_updated` webhook is the
   * authority, and claiming success here could show a hold that does not exist.
   */
  const handleRequiresAction = useCallback(
    async (clientSecret: string) => {
      if (!STRIPE_PUBLISHABLE_KEY) {
        setError(
          'Your bank needs to verify this payment, but verification is not available right now. Please contact support.'
        );
        return;
      }

      const { loadStripe } = await import('@stripe/stripe-js');
      const stripe = await loadStripe(STRIPE_PUBLISHABLE_KEY);

      if (!stripe) {
        setError('Could not load payment verification. Please try again.');
        return;
      }

      const result = await stripe.handleNextAction({ clientSecret });

      if (result.error) {
        setError(result.error.message || 'Verification was not completed.');
        return;
      }

      setSuccess(
        'Verified. Your card is authorized but has not been charged — we collect payment once the work is done.'
      );
      await fetchCharges();
    },
    [fetchCharges]
  );

  /** Send the customer to Stripe's billing portal to add a card. */
  const handleOpenBilling = async () => {
    setOpeningBilling(true);
    try {
      window.location.href = await createBillingPortalSession();
    } catch {
      setError('Could not open billing. Please contact support.');
      setOpeningBilling(false);
    }
  };

  const handleConfirm = async (charge: ServiceCharge) => {
    setConfirming(charge.id);
    setError(null);
    setSuccess(null);

    try {
      await authorizeServiceCharge(charge.id);
      setSuccess(
        'Confirmed. Your card is authorized but has not been charged — we collect payment once the work is done.'
      );
      await fetchCharges();
    } catch (err) {
      if (err instanceof RequiresActionError) {
        await handleRequiresAction(err.clientSecret);
      } else {
        const message = err instanceof Error ? err.message : 'Could not confirm this service';
        setError(message);
        // A missing card is the one failure the customer can fix themselves —
        // offer the billing portal rather than leaving them at a dead end.
        setNeedsPaymentMethod(/payment method/i.test(message));
      }
    } finally {
      setConfirming(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading services…</span>
        </div>
      </div>
    );
  }

  // Nothing to show, and nothing to explain.
  if (!charges.length && !error) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-1">
        <Receipt className="w-5 h-5 text-[#3AABE8]" />
        <h2 className="text-lg font-semibold text-gray-900">Services</h2>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        One-time setup services. You're only charged once the work is complete.
      </p>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-md bg-red-50 border border-red-200 p-3">
          <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm text-red-700">{error}</p>
            {needsPaymentMethod && (
              <button
                onClick={handleOpenBilling}
                disabled={openingBilling}
                className="mt-2 text-sm font-medium text-[#3AABE8] hover:underline disabled:opacity-60"
              >
                {openingBilling ? 'Opening…' : 'Add a payment method →'}
              </button>
            )}
          </div>
        </div>
      )}

      {success && (
        <div className="mb-4 flex items-start gap-2 rounded-md bg-green-50 border border-green-200 p-3">
          <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
          <p className="text-sm text-green-800">{success}</p>
        </div>
      )}

      <ul className="space-y-3">
        {charges.map((charge) => (
          <ServiceChargeRow
            key={charge.id}
            charge={charge}
            confirming={confirming === charge.id}
            onConfirm={() => handleConfirm(charge)}
          />
        ))}
      </ul>
    </div>
  );
}

function ServiceChargeRow({
  charge,
  confirming,
  onConfirm,
}: {
  charge: ServiceCharge;
  confirming: boolean;
  onConfirm: () => void;
}) {
  const daysLeft = daysUntilExpiry(charge.authorization_expires_at);

  return (
    <li className="flex items-start justify-between gap-4 rounded-md border border-gray-200 p-4">
      <div className="min-w-0">
        <p className="font-medium text-gray-900">{charge.description}</p>
        <p className="text-sm text-gray-500 mt-0.5">
          {formatAmount(charge.amount_cents, charge.currency)}
        </p>

        <div className="mt-2">
          <StatusLine charge={charge} daysLeft={daysLeft} />
        </div>
      </div>

      {charge.status === 'draft' && (
        <button
          onClick={onConfirm}
          disabled={confirming}
          className="shrink-0 inline-flex items-center gap-2 rounded-md bg-[#3AABE8] px-4 py-2 text-sm font-medium text-white hover:bg-[#2f97cf] disabled:opacity-60"
        >
          {confirming && <Loader2 className="w-4 h-4 animate-spin" />}
          {confirming ? 'Confirming…' : 'Confirm'}
        </button>
      )}
    </li>
  );
}

function StatusLine({
  charge,
  daysLeft,
}: {
  charge: ServiceCharge;
  daysLeft: number | null;
}) {
  if (charge.status === 'authorized') {
    return (
      <div className="flex items-start gap-2">
        <ShieldCheck className="w-4 h-4 text-[#3AABE8] mt-0.5 shrink-0" />
        <div>
          {/* The most important sentence in this component. */}
          <p className="text-sm text-gray-700">
            Reserved — we're working on it.{' '}
            <span className="font-medium">Your card has not been charged yet.</span>
          </p>
          {daysLeft !== null && (
            <p className="text-xs text-gray-500 mt-0.5">
              {daysLeft === 0
                ? 'Reservation expires today'
                : `Reservation held for ${daysLeft} more day${daysLeft === 1 ? '' : 's'}`}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (charge.status === 'captured') {
    return (
      <p className="flex items-center gap-2 text-sm text-green-700">
        <CheckCircle className="w-4 h-4 shrink-0" />
        Paid
        {charge.captured_amount_cents !== null &&
          charge.captured_amount_cents !== charge.amount_cents && (
            <span className="text-gray-500">
              ({formatAmount(charge.captured_amount_cents, charge.currency)})
            </span>
          )}
      </p>
    );
  }

  if (charge.status === 'expired') {
    return (
      <p className="flex items-center gap-2 text-sm text-amber-700">
        <Clock className="w-4 h-4 shrink-0" />
        Reservation expired — you were not charged. Please confirm again.
      </p>
    );
  }

  if (charge.status === 'failed') {
    return (
      <p className="flex items-center gap-2 text-sm text-red-700">
        <AlertCircle className="w-4 h-4 shrink-0" />
        {charge.status_label}
      </p>
    );
  }

  return <p className="text-sm text-gray-500">{charge.status_label}</p>;
}
